import { useState, useRef } from "react";
import { api, fetchBlobUrl, downloadFile, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  FileText, HelpCircle, Info, ListChecks, Paperclip,
  CalendarClock, AlertTriangle, Footprints, Volume2, Square,
  Globe, Loader2, CalendarPlus, Check,
} from "lucide-react";
import { cn } from "@/lib/utils";

const LANGS = [
  { code: "fr", name: "Français" },
  { code: "en", name: "English" },
  { code: "ar", name: "العربية" },
  { code: "es", name: "Español" },
  { code: "pt", name: "Português" },
  { code: "zh", name: "中文" },
  { code: "de", name: "Deutsch" },
];

const RISK_STYLES = {
  faible: { label: "Risque faible", cls: "bg-success/10 text-success border-success/30" },
  moyen: { label: "Risque moyen", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  eleve: { label: "Risque élevé", cls: "bg-danger/10 text-danger border-danger/30" },
};

function Block({ icon: Icon, title, n, children, tone = "brand", testid }) {
  return (
    <Card className="p-5 sm:p-6 border-border/70 adminia-fade-in" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center",
          tone === "danger" ? "bg-danger/10" : "bg-brand/10")}>
          <Icon className={cn("h-5 w-5", tone === "danger" ? "text-danger" : "text-brand")} />
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Bloc {n}</p>
          <h3 className="font-heading font-semibold text-lg leading-tight">{title}</h3>
        </div>
      </div>
      <div className="text-[15px] leading-relaxed text-foreground/90">{children}</div>
    </Card>
  );
}

export function FicheCard({ fiche, docId, isPremium, onPremiumBlocked }) {
  const [lang, setLang] = useState("fr");
  const [translating, setTranslating] = useState(false);
  const [translations, setTranslations] = useState({}); // code -> fiche
  const [speaking, setSpeaking] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const audioRef = useRef(null);

  if (!fiche) return null;
  const display = lang === "fr" ? fiche : (translations[lang] || fiche);
  const rtl = lang === "ar";

  const chooseLang = async (code) => {
    if (code === "fr") { setLang("fr"); return; }
    if (!isPremium) { onPremiumBlocked?.("L'explication multilingue est réservée aux abonnés Premium."); return; }
    if (translations[code]) { setLang(code); return; }
    if (!docId) { toast.error("Analyse à enregistrer avant traduction."); return; }
    setTranslating(true);
    try {
      const { data } = await api.post(`/documents/${docId}/translate`, { lang: code });
      setTranslations((t) => ({ ...t, [code]: data.fiche }));
      setLang(code);
    } catch {
      toast.error("La traduction a échoué.");
    } finally { setTranslating(false); }
  };

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setSpeaking(false);
  };

  const handleSpeak = async () => {
    if (!isPremium) { onPremiumBlocked?.("La lecture à voix haute est réservée aux abonnés Premium."); return; }
    if (speaking) { stopAudio(); return; }
    const text = [
      display.titre, display.resume, `On vous demande : ${display.demande}`,
      `Pourquoi : ${display.pourquoi}`,
      `Vos actions : ${(display.actions || []).join(". ")}`,
      `Risque : ${display.risque?.texte || ""}`,
      `Procédure : ${(display.procedure || []).join(". ")}`,
    ].filter(Boolean).join(". ");
    setTtsLoading(true);
    try {
      const res = await api.post("/tts", { text }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      await audio.play();
      setSpeaking(true);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(err.response?.data?.detail?.message || "Plafond quotidien de lecture vocale atteint. Réessayez demain.");
      } else {
        toast.error("La lecture a échoué.");
      }
    } finally { setTtsLoading(false); }
  };

  const addToCalendar = () => {
    const token = getToken();
    downloadFile(`/documents/${docId}/ics?auth=${token}`, `echeance_adminia.ics`)
      .catch(() => toast.error("Export du rappel impossible."));
  };

  const risk = RISK_STYLES[display.risque?.niveau] || RISK_STYLES.moyen;

  return (
    <div className="space-y-4" data-testid="fiche-adminia" dir={rtl ? "rtl" : "ltr"}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand leading-tight">
            {display.titre || "Fiche ADMINIA"}
          </h2>
          {display.expediteur && (
            <p className="text-sm text-muted-foreground mt-1">Émetteur : {display.expediteur}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="fiche-lang-btn">
                {translating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
                <span className="hidden sm:inline">{LANGS.find((l) => l.code === lang)?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => chooseLang(l.code)}
                  data-testid={`lang-${l.code}`} className="gap-2">
                  {lang === l.code && <Check className="h-4 w-4 text-brand" />}
                  <span className={lang === l.code ? "font-semibold" : ""}>{l.name}</span>
                  {l.code !== "fr" && !isPremium && <Badge variant="outline" className="ml-auto text-[10px]">Premium</Badge>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={handleSpeak}
            className="rounded-full gap-1.5" data-testid="fiche-tts-btn" disabled={ttsLoading}>
            {ttsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : speaking ? <Square className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            <span className="hidden sm:inline">{speaking ? "Stop" : "Écouter"}</span>
          </Button>
        </div>
      </div>

      <Block n={1} title="En résumé" icon={FileText} testid="block-resume">
        <p className="text-base sm:text-lg">{display.resume}</p>
      </Block>

      <div className="grid gap-4 md:grid-cols-2">
        <Block n={2} title="Ce qu'on vous demande" icon={HelpCircle} testid="block-demande">{display.demande}</Block>
        <Block n={3} title="Pourquoi" icon={Info} testid="block-pourquoi">{display.pourquoi}</Block>
      </div>

      <Block n={4} title="Vos actions, étape par étape" icon={ListChecks} testid="block-actions">
        <ol className="space-y-2">
          {(display.actions || []).map((a, i) => (
            <li key={i} className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <span>{a}</span>
            </li>
          ))}
        </ol>
      </Block>

      <Block n={5} title="Documents à fournir" icon={Paperclip} testid="block-documents">
        <div className="space-y-3">
          {(display.documents || []).map((d, i) => (
            <div key={i} className="flex gap-3 items-start p-3 rounded-lg bg-surface">
              <Checkbox id={`doc-${i}`} className="mt-1" data-testid={`doc-check-${i}`} />
              <label htmlFor={`doc-${i}`} className="cursor-pointer">
                <span className="font-medium block">{d.nom}</span>
                {d.comment_obtenir && <span className="text-sm text-muted-foreground">💡 {d.comment_obtenir}</span>}
              </label>
            </div>
          ))}
          {(!display.documents || display.documents.length === 0) && (
            <p className="text-muted-foreground text-sm">Aucun document spécifique demandé.</p>
          )}
        </div>
      </Block>

      <div className="grid gap-4 md:grid-cols-2">
        <Block n={6} title="Date limite" icon={CalendarClock} testid="block-deadline">
          {display.date_limite?.date ? (
            <>
              <p className="font-semibold text-lg">
                {new Date(display.date_limite.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
              </p>
              {docId && (
                <Button size="sm" variant="outline" className="rounded-full gap-1.5 mt-3" onClick={addToCalendar} data-testid="fiche-ics-btn">
                  <CalendarPlus className="h-3.5 w-3.5" /> Ajouter à mon calendrier
                </Button>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">Non précisée dans le courrier.</p>
          )}
          {display.date_limite?.texte && (
            <p className="text-sm text-muted-foreground mt-2">« {display.date_limite.texte} »</p>
          )}
        </Block>
        <Block n={7} title="Risque en cas d'inaction" icon={AlertTriangle} tone="danger" testid="block-risque">
          <Badge variant="outline" className={cn("mb-2", risk.cls)}>{risk.label}</Badge>
          <p>{display.risque?.texte}</p>
        </Block>
      </div>

      <Block n={8} title="La procédure pas-à-pas" icon={Footprints} testid="block-procedure">
        <ol className="relative border-l-2 border-brand/20 ml-2 space-y-4">
          {(display.procedure || []).map((p, i) => (
            <li key={i} className="ml-4 relative">
              <span className="absolute -left-[22px] top-1 h-3 w-3 rounded-full bg-brand" />
              {p}
            </li>
          ))}
        </ol>
      </Block>

      <p className="text-xs text-muted-foreground text-center pt-2">
        ⚠️ Information générée pour vous aider à comprendre. À vérifier sur le site officiel de l'organisme.
      </p>
    </div>
  );
}
